import React from "react"
import { Chip, Tooltip } from "@mui/material"
import EmergencyIcon from "@mui/icons-material/Emergency"

type PatientChipSize = "small" | "medium" | "large"

export interface PatientChipProps {
  label: string
  size?: PatientChipSize
  showTooltip?: boolean
}

const PATIENT_CHIP_SIZE_STYLES: Record<PatientChipSize, { px: number; py: number; fontSize: string }> = {
  small: {
    px: 0.15,
    py: 0.5,
    fontSize: "0.64rem",
  },
  medium: {
    px: 0.3,
    py: 1,
    fontSize: "0.9rem",
  },
  large: {
    px: 0.75,
    py: 2,
    fontSize: "1.25rem",
  },
}

/**
 * Compact patient identifier chip used in the meeting flow summary headers.
 */
const PatientChip: React.FC<PatientChipProps> = ({
  label,
  size = "large",
  showTooltip = true,
}: PatientChipProps) => {
  const sizeStyle = PATIENT_CHIP_SIZE_STYLES[size]

  const tagBaseSx = {
    px: sizeStyle.px,
    py: sizeStyle.py,
    fontSize: sizeStyle.fontSize,
    fontWeight: 600,
    borderRadius: 999,
  }

  const chip = (
    <Chip
      icon={<EmergencyIcon fontSize="inherit" />}
      label={label}
      variant="filled"
      size="small"
      sx={(theme) => ({
        ...tagBaseSx,
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: theme.palette.patientChip.background,
        color: theme.palette.patientChip.main,
        fontFamily: "monospace",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        ".MuiChip-label": {
          display: "flex",
          alignItems: "center",
          lineHeight: 1,
        },
        ".MuiChip-icon": {
          color: theme.palette.patientChip.main,
          fontSize: sizeStyle.fontSize,
          alignSelf: "center",
        },
      })}
    />
  )

  if (!showTooltip) {
    return chip
  }

  return (
    <Tooltip title="Patient ID" placement="top" arrow>
      {chip}
    </Tooltip>
  )
}

export default PatientChip
